'use client';

import { useEffect, useMemo, useState } from 'react';

import Button from 'components/Button';
import { JitsiVideoEmbedDirect } from 'components/videoEmbeders/JitsiVideoEmbed/JitsiVideoEmbedDirect';
import JitsiVideoEmbedMobile from 'components/videoEmbeders/JitsiVideoEmbed/JitsiVideoEmbedMobile';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useUser } from 'global/UserContext';
import { useWebSocket } from 'global/WebSocketContext';
import { useIframeCommunication } from 'hooks/useIframeCommunication';
import { buildThemeParams } from 'microfrontends/config/theme';
import { getOrganizationUuid } from 'microfrontends/utils/params';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import Scheduling from 'pages/AgendaConfiguration/components/Scheduling';
import { useIsMobile } from 'utils/mediaQueries';

function getOtherMeetingUser(meeting: any, userId: string) {
  if (!meeting || !userId) return null;
  if (meeting.host?.uuid === userId) {
    return meeting.requester;
  }
  if (meeting.requester?.uuid === userId) {
    return meeting.host;
  }
  return null;
}

const AccessHub = () => {
  const { user } = useUser();
  const theme = useThemeContext();
  const { lastMessage } = useWebSocket();

  const userId = user?.id;

  const themeParams = useMemo(() => buildThemeParams(theme), [theme]);

  const baseUrl = useMemo(() => {
    const orgUuid = getOrganizationUuid();
    return `${process.env.REACT_APP_URL_MEETING_PLATFORM}/access-hub?userId=${userId}&organization_uuid=${orgUuid}`;
  }, [userId]);

  const iframeUrl = useMemo(() => `${baseUrl}&${themeParams}`, [baseUrl, themeParams]);

  const {
    iframeRef,
    sendMessageToIframe,
    iframeReady,
    connectionStatus,
    handleIframeLoad,
    handleIframeError,
    rescheduleMeetingPayload,
    accessRoomPayload,
    setAccessRoomPayload,
  } = useIframeCommunication(iframeUrl);

  const [openScheduling, setOpenScheduling] = useState(false);
  const [schedulingData, setSchedulingData] = useState<{
    requesterId: string;
    hostId: string;
    hostName: string;
  } | null>(null);

  const isMobile = useIsMobile();
  const [openRoom, setOpenRoom] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const otherUser = getOtherMeetingUser(rescheduleMeetingPayload?.meeting, userId || '');
  const otherUserName = otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : '';

  useEffect(() => {
    if (lastMessage) {
      console.log('[AccessHub] New message:', lastMessage);
      if (iframeRef.current) {
        iframeRef.current.src = iframeRef.current.src;
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    if (rescheduleMeetingPayload) {
      if (rescheduleMeetingPayload.meetingId && rescheduleMeetingPayload.hostId && userId) {
        setSchedulingData({
          requesterId: userId,
          hostId: rescheduleMeetingPayload.hostId,
          hostName: rescheduleMeetingPayload.hostName,
        });
        setOpenScheduling(true);
      } else {
        console.warn('[AccessHub] Payload incompleto ou userId não disponível:', {
          payload: rescheduleMeetingPayload,
          userId,
        });
      }
    }
  }, [rescheduleMeetingPayload, userId]);

  useEffect(() => {
    if (accessRoomPayload) {
      setOpenRoom(true);
    }
  }, [accessRoomPayload]);

  const handleCloseScheduling = () => {
    setOpenScheduling(false);
    setSchedulingData(null);
  };

  const handleCloseRoom = () => {
    setOpenRoom(false);
    setAccessRoomPayload(null);
  };

  const handleCloseRoomWithConfirmation = () => {
    setShowCloseConfirmation(true);
  };

  const confirmCloseRoom = () => {
    setShowCloseConfirmation(false);
    handleCloseRoom();
  };

  const cancelCloseRoom = () => {
    setShowCloseConfirmation(false);
  };

  const getNormalizedMeetingRoom = (meetingRoom: any) => {
    if (!meetingRoom) return undefined;
    if (meetingRoom.participation_status) return meetingRoom;
    if (meetingRoom.participant_status) {
      return {
        ...meetingRoom,
        participation_status: meetingRoom.participant_status,
      };
    }
    return meetingRoom;
  };

  const normalizedMeetingRoom = getNormalizedMeetingRoom(accessRoomPayload?.meetingRoom);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '85vh',
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title="Meeting Platform - Access Hub"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />

      <GlobalModal
        open={openRoom && !!accessRoomPayload}
        onClose={handleCloseRoom}
        width={isMobile ? '100vw' : '70vw'}
        height={isMobile ? '100vh' : '90vh'}
        title={
          `${accessRoomPayload?.meetingRoom?.room_data?.description} | ${accessRoomPayload?.meeting?.host?.first_name} ${accessRoomPayload?.meeting?.host?.last_name} - ${accessRoomPayload?.meeting?.requester?.first_name} ${accessRoomPayload?.meeting?.requester?.last_name}` ||
          'Sala de Reunião'
        }
        keepMounted
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {!isMobile ? (
              normalizedMeetingRoom && normalizedMeetingRoom.room_data ? (
                <JitsiVideoEmbedDirect
                  meetingRoom={normalizedMeetingRoom}
                  videoLoading={videoLoading}
                  onLoad={() => setVideoLoading(false)}
                  onClose={handleCloseRoom}
                />
              ) : (
                <div>Dados da sala indisponíveis.</div>
              )
            ) : normalizedMeetingRoom && normalizedMeetingRoom.room_data ? (
              <JitsiVideoEmbedMobile
                meetingRoom={normalizedMeetingRoom}
                onClose={handleCloseRoom}
              />
            ) : (
              <div>Dados da sala indisponíveis.</div>
            )}
          </div>

          {!isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                backgroundColor: 'transparent',
              }}
            >
              <Button variant="red" size="medium" onClick={handleCloseRoomWithConfirmation}>
                Sair da Reunião
              </Button>
            </div>
          )}
        </div>
      </GlobalModal>

      <GlobalModal
        open={showCloseConfirmation}
        title="Confirmar Saída"
        description="Tem certeza que deseja sair da reunião? Esta ação irá desconectar você da sala."
        onClose={cancelCloseRoom}
        onCancel={cancelCloseRoom}
        onConfirm={confirmCloseRoom}
        cancelButtonText="Cancelar"
        confirmButtonText="Sair da Reunião"
        width="32rem"
        height="auto"
      />

      <GlobalModal
        open={openScheduling}
        title="Reagendar Reunião"
        onClose={handleCloseScheduling}
        width="32rem"
        height="auto"
        keepMounted
      >
        {schedulingData && (
          <Scheduling
            requesterId={schedulingData.requesterId}
            hostId={schedulingData.hostId}
            onClose={handleCloseScheduling}
            hostName={otherUserName}
          />
        )}
      </GlobalModal>
    </div>
  );
};

export default AccessHub;
